import { useMutation } from '@apollo/client';
import { ACCOUNT_BUYER_GET_ANIMALS } from '@application/api';
import { graphql } from '@gql/gql';
import type {
  AnimalStatCreatedFromEnum,
  MtAnimalBuyerCreateAnimalMutation,
  MtAnimalBuyerCreateAnimalMutationVariables,
} from '@gql/graphql';
import type { BuyerAnimalFormValues } from '@presentation/components';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { useAnimalCreateBuilder } from './useAnimalCreateBuilder';

export const MT_ANIMAL_BUYER_CREATE_ANIMAL = graphql(`
  mutation MtAnimalBuyerCreateAnimal(
    $data: AnimalCreateInput!
    $imageUpload: Upload
    $createdFrom: AnimalStatCreatedFromEnum
  ) {
    createAnimal(data: $data, imageUpload: $imageUpload, createdFrom: $createdFrom) {
      ...BuyerAnimalInfo
    }
  }
`);

export interface CreateAnimalFromFormParams {
  form: BuyerAnimalFormValues;
  createdFrom?: AnimalStatCreatedFromEnum;
}
export const useCreateAnimal = () => {
  const [createAnimal, { error, loading }] = useMutation<
    MtAnimalBuyerCreateAnimalMutation,
    MtAnimalBuyerCreateAnimalMutationVariables
  >(MT_ANIMAL_BUYER_CREATE_ANIMAL);

  const { animalCreateBuilder } = useAnimalCreateBuilder();

  const createAnimalFromForm = useCallback(
    (params: CreateAnimalFromFormParams) => {
      const { form, createdFrom } = params;
      const { data } = animalCreateBuilder(form);

      const imagesUpload: Scalar_Upload[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? undefined : (file as Scalar_Upload)))
        .filter((file) => file != null) as Scalar_Upload[];

      return createAnimal({
        awaitRefetchQueries: true,
        refetchQueries: [ACCOUNT_BUYER_GET_ANIMALS],
        variables: {
          ...(imagesUpload?.[0] && { imageUpload: imagesUpload[0] }),
          ...(createdFrom && { createdFrom }),
          data,
        },
      });
    },
    [animalCreateBuilder, createAnimal],
  );

  return {
    createAnimalLoading: loading,
    createAnimalError: error,
    createAnimalFromForm,
    createAnimal,
  };
};
